/**
 * Composant Sidebar
 *
 * Navigation verticale violette moderne avec icônes
 * Design inspiré du mockup de référence
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Headphones,
  CheckCircle2,
  Package,
  FileText,
  LogOut,
  Rocket,
  Zap,
  RefreshCw,
  Users2,
  Monitor,
  BookOpen,
  HelpCircle
} from 'lucide-react';
import ProjectSwitcher from './ProjectSwitcher';

const Sidebar = ({ isAdmin = false, user, isCollapsed = false, onExpand, onCollapse, onLogout, onRefresh, onStartTour, isRefreshing = false, openTicketsCount = 0, activeUserStoriesCount = 0, testClientStoriesCount = 0, isInternalUser = false, isMobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Items de navigation selon le type d'utilisateur
  const navItems = isInternalUser
    ? [
        // Utilisateurs internes (Dynamic Horizon) : écran équipe en premier
        {
          id: 'team-tv',
          label: 'Écran équipe',
          icon: Monitor,
          path: '/team-tv',
          active: location.pathname === '/team-tv',
          badge: 0
        },
        {
          id: 'sprint',
          label: 'Analyse Sprint',
          icon: Zap,
          path: '/dashboard/sprint',
          active: location.pathname === '/dashboard/sprint',
          badge: 0
        },
        {
          id: 'team-performance',
          label: 'Performance équipe',
          icon: Users2,
          path: '/dashboard/team-performance',
          active: location.pathname === '/dashboard/team-performance',
          badge: 0
        },
        {
          id: 'tickets',
          label: 'Assistance',
          icon: Headphones,
          path: '/dashboard/tickets',
          active: location.pathname === '/dashboard/tickets',
          badge: openTicketsCount
        },
        {
          id: 'deploiements',
          label: 'Déploiements',
          icon: Rocket,
          path: '/dashboard/deploiements',
          active: location.pathname === '/dashboard/deploiements',
          badge: 0
        },
        {
          id: 'livraisons',
          label: 'Livraisons',
          icon: Package,
          path: '/dashboard/livraisons',
          active: location.pathname === '/dashboard/livraisons',
          badge: 0
        },
        {
          id: 'documentation',
          label: 'Documentation',
          icon: BookOpen,
          path: '/dashboard/documentation',
          active: location.pathname === '/dashboard/documentation',
          badge: 0
        }
      ]
    : [
        // Utilisateurs clients : Accueil + Assistance + Tests à valider
        {
          id: 'dashboard',
          label: 'Accueil',
          icon: Home,
          path: '/dashboard',
          active: location.pathname === '/dashboard',
          badge: activeUserStoriesCount
        },
        {
          id: 'tickets',
          label: 'Assistance',
          icon: Headphones,
          path: '/dashboard/tickets',
          active: location.pathname === '/dashboard/tickets',
          badge: openTicketsCount
        },
        {
          id: 'testing',
          label: 'Tests à valider',
          icon: CheckCircle2,
          path: '/dashboard/client-testing',
          active: location.pathname === '/dashboard/client-testing',
          badge: testClientStoriesCount
        },
        {
          id: 'deploiements',
          label: 'Déploiements',
          icon: Rocket,
          path: '/dashboard/deploiements',
          active: location.pathname === '/dashboard/deploiements',
          badge: 0
        },
        {
          id: 'livraisons',
          label: 'Livraisons',
          icon: Package,
          path: '/dashboard/livraisons',
          active: location.pathname === '/dashboard/livraisons',
          badge: 0
        },
      ];

  if (isInternalUser) {
    navItems.splice(1, 0, {
      id: 'dashboard', label: 'Accueil', icon: Home,
      path: '/dashboard', active: location.pathname === '/dashboard',
      badge: activeUserStoriesCount
    });
    const ticketsIndex = navItems.findIndex(item => item.id === 'tickets');
    navItems.splice(ticketsIndex + 1, 0, {
      id: 'testing', label: 'Tests à valider', icon: CheckCircle2,
      path: '/dashboard/client-testing', active: location.pathname === '/dashboard/client-testing',
      badge: testClientStoriesCount
    });
  }

  if (!isInternalUser) navItems.push({ id: 'documentation', label: 'Documentation', icon: BookOpen, path: '/dashboard/documentation', active: location.pathname === '/dashboard/documentation', badge: 0 });

  if (isAdmin || isInternalUser) navItems.splice(navItems.findIndex(item => item.id === 'dashboard') + 1, 0, { id: 'ticket-situation', label: 'Situation des tickets', icon: FileText, path: '/dashboard/ticket-situation', active: location.pathname === '/dashboard/ticket-situation', badge: 0 });

  const handleNavigation = (path) => {
    navigate(path);
    onMobileClose?.();
  };

  return (
    <aside aria-label="Menu principal" onMouseEnter={onExpand} onMouseLeave={onCollapse}
      onFocus={onExpand} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) onCollapse?.(); }}
      data-collapsed={isCollapsed} data-horizon={['/dashboard', '/dashboard/tickets', '/dashboard/client-testing', '/dashboard/ticket-situation', '/dashboard/user-stories-status', '/dashboard/milestone-stories', '/dashboard/deploiements', '/dashboard/livraisons', '/dashboard/chatbot'].includes(location.pathname) || (!isInternalUser && ['/', '/dashboard/documentation'].includes(location.pathname)) ? 'true' : undefined}
      className={`
        fixed left-0 top-0 z-50 flex h-screen flex-col overflow-hidden
        bg-[#382443] text-white shadow-[14px_0_40px_rgba(42,25,51,0.16)]
        transition-[width,transform] duration-300 ease-out
        ${isCollapsed ? 'lg:w-20' : 'lg:w-64'} w-72
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
      `}
    >
      <div className="horizon-sidebar-brand border-b border-white/10 px-4 py-5">
        <div className="flex items-center justify-between gap-2">
          <div className={`horizon-logo-container ${isCollapsed ? 'is-compact' : ''}`}>
            <img src="/assets/logo.png" alt="Dynamic Horizon" className="horizon-logo" />
          </div>
        </div>
        {!isCollapsed && user?.name && <p className="horizon-session" title={user.name}>{user.name}</p>}
      </div>

      {/* Project Switcher */}
      <ProjectSwitcher isCollapsed={isCollapsed} />

      {/* Navigation Items */}
      <nav data-tour="navigation" className="flex-1 overflow-y-auto px-3 py-5 space-y-1.5" aria-label="Navigation principale">
        {navItems.map((item) => {
          const Icon = item.icon;
          const hasBadge = item.badge !== undefined && item.badge > 0;

          return (
            <button
              key={item.id}
              aria-label={item.label}
              aria-current={item.active ? 'page' : undefined}
              onClick={() => handleNavigation(item.path)}
              className={`
                group relative flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left
                transition-colors duration-150
                ${item.active
                  ? 'bg-white text-[#523467] shadow-[0_8px_22px_rgba(20,10,28,0.18)]'
                  : 'text-white/70 hover:bg-white/[0.08] hover:text-white'
                }
                ${isCollapsed ? 'justify-center' : ''}
              `}
              title={isCollapsed ? item.label : ''}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && (
                <span className="font-medium text-sm">{item.label}</span>
              )}
              {!isCollapsed && hasBadge && (
                <span className="ml-auto min-w-[22px] rounded-full bg-[#C94B72] px-1.5 py-0.5 text-center text-[11px] font-bold text-white">
                  {item.badge}
                </span>
              )}
              {isCollapsed && hasBadge && (
                <span className="horizon-nav-count">
                  {item.badge}
                </span>
              )}

            </button>
          );
        })}
      </nav>

      {/* Bottom Actions */}
      <div className="space-y-1 border-t border-white/10 p-3">
        {onStartTour && (
          <button type="button" onClick={onStartTour} aria-label="Guide de l’interface"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/75 hover:bg-white/[0.08] hover:text-white transition-all duration-200 ${isCollapsed ? 'justify-center' : ''}`}
            title={isCollapsed ? 'Guide de l’interface' : ''}>
            <HelpCircle className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && <span className="font-medium text-sm">Guide de l’interface</span>}
          </button>
        )}
        {/* Settings - Masqué pour le moment */}
        {/* <button
          className={`
            w-full flex items-center gap-3 px-4 py-3 rounded-lg
            text-brand-brand-purple-100 hover:bg-white/10 hover:text-white
            transition-all duration-200
            ${isCollapsed ? 'justify-center' : ''}
          `}
          title={isCollapsed ? 'Paramètres' : ''}
        >
          <Settings className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && <span className="font-medium text-sm">Paramètres</span>}
        </button> */}

        {/* Refresh */}
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className={`
              w-full flex items-center gap-3 px-4 py-3 rounded-lg
              text-white/65 hover:bg-white/[0.08] hover:text-white
              transition-all duration-200
              ${isCollapsed ? 'justify-center' : ''}
              ${isRefreshing ? 'opacity-60 cursor-not-allowed' : ''}
            `}
            title={isCollapsed ? 'Actualiser' : ''}
          >
            <RefreshCw className={`w-5 h-5 flex-shrink-0 ${isRefreshing ? 'animate-spin' : ''}`} />
            {!isCollapsed && <span className="font-medium text-sm">Actualiser</span>}
          </button>
        )}

        {/* Logout */}
        {onLogout && (
          <button
            onClick={onLogout}
            className={`
              w-full flex items-center gap-3 px-4 py-3 rounded-lg
              text-white/65 hover:bg-[#C94B72] hover:text-white
              transition-all duration-200
              ${isCollapsed ? 'justify-center' : ''}
            `}
            title={isCollapsed ? 'Déconnexion' : ''}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && <span className="font-medium text-sm">Déconnexion</span>}
          </button>
        )}


      </div>
    </aside>
  );
};

export default Sidebar;
